/**
 * Script Context API
 *
 * Provides a Postman-compatible pm.* API for pre-request and post-request scripts.
 */

import { HttpRequest, HttpResponse, KeyValue, Variable, Environment } from '../../stores';

export interface ScriptExecutionContext {
  request: HttpRequest;
  response?: HttpResponse;
  environment?: Environment;
  globalEnvironment?: Environment;
  collectionVariables?: Variable[];
}

export interface ScriptConsole {
  log: (...args: any[]) => void;
  info: (...args: any[]) => void;
  warn: (...args: any[]) => void;
  error: (...args: any[]) => void;
}

export interface TestResult {
  name: string;
  passed: boolean;
  error?: string;
}

export interface ScriptResult {
  success: boolean;
  logs: Array<{ level: string; args: any[] }>;
  tests: TestResult[];
  error?: string;
  modifiedRequest?: Partial<HttpRequest>;
  variableUpdates: {
    environment: Record<string, string>;
    collection: Record<string, string>;
    global: Record<string, string>;
  };
}

/**
 * Create the pm.* API object for script execution
 */
export function createPmApi(
  context: ScriptExecutionContext,
  result: ScriptResult
): Record<string, any> {
  const { request, response, environment, globalEnvironment, collectionVariables } = context;

  // Environment variable helpers
  const envVars = new Map<string, string>();
  environment?.variables?.forEach((v) => {
    if (v.enabled) envVars.set(v.key, v.value);
  });

  const globalVars = new Map<string, string>();
  globalEnvironment?.variables?.forEach((v) => {
    if (v.enabled) globalVars.set(v.key, v.value);
  });

  const colVars = new Map<string, string>();
  collectionVariables?.forEach((v) => {
    if (v.enabled) colVars.set(v.key, v.value);
  });

  return {
    // Environment
    environment: {
      get: (key: string) => envVars.get(key) ?? result.variableUpdates.environment[key],
      set: (key: string, value: string) => {
        result.variableUpdates.environment[key] = value;
        envVars.set(key, value);
      },
      unset: (key: string) => {
        delete result.variableUpdates.environment[key];
        envVars.delete(key);
      },
      has: (key: string) => envVars.has(key) || key in result.variableUpdates.environment,
    },

    // Collection Variables
    collectionVariables: {
      get: (key: string) => colVars.get(key) ?? result.variableUpdates.collection[key],
      set: (key: string, value: string) => {
        result.variableUpdates.collection[key] = value;
        colVars.set(key, value);
      },
      unset: (key: string) => {
        delete result.variableUpdates.collection[key];
        colVars.delete(key);
      },
      has: (key: string) => colVars.has(key) || key in result.variableUpdates.collection,
    },

    // Global Variables
    globals: {
      get: (key: string) => globalVars.get(key) ?? result.variableUpdates.global[key],
      set: (key: string, value: string) => {
        result.variableUpdates.global[key] = value;
        globalVars.set(key, value);
      },
      unset: (key: string) => {
        delete result.variableUpdates.global[key];
        globalVars.delete(key);
      },
      has: (key: string) => globalVars.has(key) || key in result.variableUpdates.global,
    },

    // Request (for pre-request scripts)
    request: {
      url: request.url,
      method: request.method,
      headers: {
        add: (name: string, value: string) => {
          if (!result.modifiedRequest) result.modifiedRequest = {};
          if (!result.modifiedRequest.headers) {
            result.modifiedRequest.headers = [...request.headers];
          }
          result.modifiedRequest.headers.push({ key: name, value, enabled: true });
        },
        get: (name: string) => {
          const header = request.headers.find(
            (h) => h.key.toLowerCase() === name.toLowerCase()
          );
          return header?.value;
        },
        remove: (name: string) => {
          if (!result.modifiedRequest) result.modifiedRequest = {};
          if (!result.modifiedRequest.headers) {
            result.modifiedRequest.headers = [...request.headers];
          }
          result.modifiedRequest.headers = result.modifiedRequest.headers.filter(
            (h) => h.key.toLowerCase() !== name.toLowerCase()
          );
        },
      },
      body: request.body?.raw?.content,
    },

    // Response (for post-request scripts)
    response: response
      ? {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
          responseTime: response.time,
          responseSize: response.size,
          text: () => response.body,
          json: () => {
            try {
              return JSON.parse(response.body);
            } catch {
              throw new Error('Response body is not valid JSON');
            }
          },
        }
      : undefined,

    // Testing
    test: (name: string, fn: () => void) => {
      try {
        fn();
        result.tests.push({ name, passed: true });
      } catch (e) {
        result.tests.push({
          name,
          passed: false,
          error: e instanceof Error ? e.message : String(e),
        });
      }
    },

    // Chai-style expect
    expect: (value: any) => createExpect(value),

    // Info
    info: {
      requestName: request.name,
      requestId: request.id,
    },
  };
}

/**
 * Create a simple Chai-style expect API
 */
function createExpect(value: any) {
  const assert = (condition: boolean, message: string) => {
    if (!condition) throw new Error(message);
  };

  const expectObj: Record<string, any> = {
    to: {} as Record<string, any>,
  };

  // Direct assertions
  expectObj.to.equal = (expected: any) => {
    assert(value === expected, `Expected ${value} to equal ${expected}`);
    return expectObj;
  };

  expectObj.to.eql = (expected: any) => {
    assert(
      JSON.stringify(value) === JSON.stringify(expected),
      `Expected ${JSON.stringify(value)} to deeply equal ${JSON.stringify(expected)}`
    );
    return expectObj;
  };

  expectObj.to.be = {
    a: (type: string) => {
      assert(typeof value === type, `Expected ${value} to be a ${type}`);
      return expectObj;
    },
    an: (type: string) => {
      assert(typeof value === type, `Expected ${value} to be an ${type}`);
      return expectObj;
    },
    true: () => {
      assert(value === true, `Expected ${value} to be true`);
      return expectObj;
    },
    false: () => {
      assert(value === false, `Expected ${value} to be false`);
      return expectObj;
    },
    null: () => {
      assert(value === null, `Expected ${value} to be null`);
      return expectObj;
    },
    undefined: () => {
      assert(value === undefined, `Expected ${value} to be undefined`);
      return expectObj;
    },
    above: (num: number) => {
      assert(value > num, `Expected ${value} to be above ${num}`);
      return expectObj;
    },
    below: (num: number) => {
      assert(value < num, `Expected ${value} to be below ${num}`);
      return expectObj;
    },
    oneOf: (list: any[]) => {
      assert(list.includes(value), `Expected ${value} to be one of ${list}`);
      return expectObj;
    },
  };

  expectObj.to.have = {
    property: (prop: string) => {
      assert(prop in value, `Expected object to have property '${prop}'`);
      return expectObj;
    },
    lengthOf: (len: number) => {
      assert(value.length === len, `Expected length ${value.length} to be ${len}`);
      return expectObj;
    },
  };

  expectObj.to.include = (item: any) => {
    if (typeof value === 'string') {
      assert(value.includes(item), `Expected '${value}' to include '${item}'`);
    } else if (Array.isArray(value)) {
      assert(value.includes(item), `Expected array to include ${item}`);
    }
    return expectObj;
  };

  expectObj.to.exist = () => {
    assert(value !== null && value !== undefined, `Expected ${value} to exist`);
    return expectObj;
  };

  // Negation
  expectObj.to.not = {
    equal: (expected: any) => {
      assert(value !== expected, `Expected ${value} to not equal ${expected}`);
      return expectObj;
    },
    be: {
      null: () => {
        assert(value !== null, `Expected ${value} to not be null`);
        return expectObj;
      },
      undefined: () => {
        assert(value !== undefined, `Expected ${value} to not be undefined`);
        return expectObj;
      },
    },
    include: (item: any) => {
      if (typeof value === 'string') {
        assert(!value.includes(item), `Expected '${value}' to not include '${item}'`);
      } else if (Array.isArray(value)) {
        assert(!value.includes(item), `Expected array to not include ${item}`);
      }
      return expectObj;
    },
  };

  return expectObj;
}

/**
 * Create a console object that captures logs
 */
export function createScriptConsole(result: ScriptResult): ScriptConsole {
  return {
    log: (...args) => result.logs.push({ level: 'log', args }),
    info: (...args) => result.logs.push({ level: 'info', args }),
    warn: (...args) => result.logs.push({ level: 'warn', args }),
    error: (...args) => result.logs.push({ level: 'error', args }),
  };
}
